import FormControlLabel from '@mui/material/FormControlLabel';
import { Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import { useOnboardingContext } from '@swf/components/Onboarding/OnboardingContext/OnbordingContext';
import { select } from '@swf/core/store';

import ChipIcon from '../../assets/play_money_chip.svg';
import { CashierOnboardingStep } from '../WalletHomeContainer/cashierOnboardingStepsInfo';

import { TXTCLI_Play_Money, TXTWCL_Available_balance } from './WalletSummaryPanel.messages.json';
import {
  activeStepTour,
  balance,
  subtitle,
  subtitleContainer,
  toggleLabel,
  toggleRoot
} from './WalletSummaryPanel.scss';
import BalanceBreakdown from './components/BalanceBreakdown/BalanceBreakdown';
import PlayMoneySection from './components/PlayMoneySection/PlayMoneySection';
import PlayMoneyToggle from './components/PlayMoneyToggle/PlayMoneyToggle';

const WalletSummaryPanel: React.FC = () => {
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const [showPlayMoney, setShowPlayMoney] = useState(
    isPlayMoney || (isEmbedded && canUseDOM && !!window?.starsweb?.app.playMoney)
  );
  const totalBalance = useSelector(select.account.getTotalBalance);
  const playChips = useSelector(select.account.getPlayChips);
  const playMoneyFlag = useFeature(FeatureFlag.Wc2PlayMoney);
  const { setFlowToStep, activeStep, onboardingFinished } = useOnboardingContext();

  useEffect(() => {
    const updateShowPlayMoney = () => {
      if (!canUseDOM) return;

      if (isEmbedded) {
        setShowPlayMoney(!!window.starsweb?.app.playMoney);
      }
    };

    document.addEventListener('starsplaymoney', updateShowPlayMoney);
    return () => document.removeEventListener('starsplaymoney', updateShowPlayMoney);
  }, [isEmbedded]);

  return (
    <div
      className={
        !onboardingFinished && activeStep === CashierOnboardingStep.StepSummaryPanel
          ? activeStepTour
          : ''
      }
      ref={
        !onboardingFinished && setFlowToStep
          ? setFlowToStep(CashierOnboardingStep.StepSummaryPanel)
          : null
      }
    >
      <div className={subtitleContainer}>
        <div className={subtitle}>
          <Translation {...TXTWCL_Available_balance} />
        </div>
        {playMoneyFlag && (
          <FormControlLabel
            control={
              <PlayMoneyToggle
                onChange={(e) => setShowPlayMoney(e.target.checked)}
                isPlayMoney={showPlayMoney}
              />
            }
            label={<Translation {...TXTCLI_Play_Money} />}
            labelPlacement="start"
            classes={{ label: toggleLabel, root: toggleRoot }}
          />
        )}
      </div>
      <div className={balance} data-testid="wallet-balance">
        {showPlayMoney ? (
          <>
            <CurrencyAmount value={playChips as number} currency={Currency.PlayMoney} />{' '}
            <ChipIcon />
          </>
        ) : (
          <CurrencyAmount value={totalBalance as number} />
        )}
      </div>
      {showPlayMoney ? <PlayMoneySection /> : <BalanceBreakdown />}
    </div>
  );
};

export default WalletSummaryPanel;
